import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { authenticatedRequest } from "../../../config/axios";
import { documentView, getAllDocuments, getMriDocuments } from "../../../config/service";

export default function ViewDocument(props) {
  const { t } = useTranslation();
  const { meetingid, clientId, meetingType, documentModal, setDocumentModal, setPacseeDocument } =
    props;
  const [documents, setDocuments] = useState([]);
  const [imagingDocs, setImagingDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [lastUploadResult, setLastUploadResult] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [autoShowUploadHint, setAutoShowUploadHint] = useState(false);
  const [isDraggingRegular, setIsDraggingRegular] = useState(false);
  const [isDraggingImaging, setIsDraggingImaging] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [typeHistory, setTypeHistory] = useState([]);
  const [typeHistoryIndex, setTypeHistoryIndex] = useState(-1);
  const fileInputRef = useRef(null);
  const imagingInputRef = useRef(null);
  const entryHandledRef = useRef("");

  const loadAllFiles = async () => {
    if (!meetingid) return;
    setLoading(true);
    try {
      const [docsRes, mriRes] = await Promise.allSettled([
        getAllDocuments(meetingid),
        getMriDocuments(meetingid),
      ]);
      const nextDocs =
        docsRes.status === "fulfilled"
          ? docsRes.value?.data?.meetingDocuments || []
          : [];
      const nextMri =
        mriRes.status === "fulfilled"
          ? mriRes.value?.data?.pacseeFiles || []
          : [];
      setDocuments(nextDocs);
      setImagingDocs(nextMri);
      if (setPacseeDocument) {
        setPacseeDocument(nextMri);
      }
    } catch (err) {
      // keep UI resilient
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!documentModal || !meetingid) return;
    loadAllFiles();
  }, [documentModal, meetingid]);

  useEffect(() => {
    if (!documentModal || !meetingid) return;
    const entryMode = props.entryMode || "view_or_upload";
    const gateKey = `${meetingid}-${entryMode}`;
    if (entryHandledRef.current === gateKey) return;
    if (entryMode === "upload") {
      setAutoShowUploadHint(true);
      fileInputRef.current?.click();
      entryHandledRef.current = gateKey;
      return;
    }
    if (
      entryMode === "view_or_upload" &&
      !loading &&
      documents.length === 0 &&
      imagingDocs.length === 0
    ) {
      setAutoShowUploadHint(true);
      fileInputRef.current?.click();
      entryHandledRef.current = gateKey;
    }
  }, [documentModal, meetingid, props.entryMode, loading, documents.length, imagingDocs.length]);

  useEffect(() => {
    if (!documentModal) {
      entryHandledRef.current = "";
      setAutoShowUploadHint(false);
    }
  }, [documentModal]);

  const uploadFiles = async (files) => {
    if (!files?.length || !meetingid || !clientId) return;
    const uploadId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setUploadQueue((prev) => [
      ...prev,
      {
        id: uploadId,
        startedAt: Date.now(),
        fileCount: files.length,
        names: files.slice(0, 3).map((f) => f.name),
        status: "uploading",
      },
    ]);
    const formData = new FormData();
    files.forEach((file) => formData.append("document", file, file.name));
    formData.append("meetingId", meetingid);
    formData.append("clientId", clientId);
    try {
      setUploadLoading(true);
      const response = await authenticatedRequest({
        method: "post",
        url: "/users/uploadDocuments",
        data: formData,
      });
      if ([200, 201, 202].includes(response?.data?.code)) {
        toast.success(t(response.data.msg));
        setAutoShowUploadHint(false);
        setLastUploadResult({
          type: "success",
          message: `${files.length} file${files.length > 1 ? "s" : ""} uploaded`,
        });
        setUploadQueue((prev) =>
          prev.map((item) =>
            item.id === uploadId ? { ...item, status: "done", finishedAt: Date.now() } : item
          )
        );
        await loadAllFiles();
      }
    } catch (err) {
      setLastUploadResult({
        type: "error",
        message: `Upload failed for ${files.length} file${files.length > 1 ? "s" : ""}`,
      });
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.id === uploadId ? { ...item, status: "failed", finishedAt: Date.now() } : item
        )
      );
    } finally {
      setUploadLoading(false);
    }
  };

  const onPickFiles = (event, targetType = "regular") => {
    const files = Array.from(event.target?.files || []);
    prepareUploadFlow(files, targetType);
    event.target.value = "";
  };

  const prepareUploadFlow = (files, targetType) => {
    if (!files?.length) return;
    setPendingUpload({ files, targetType });
    setTypeHistory([targetType]);
    setTypeHistoryIndex(0);
  };

  const onDropFiles = (event, targetType = "regular") => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingRegular(false);
    setIsDraggingImaging(false);
    const files = Array.from(event.dataTransfer?.files || []);
    prepareUploadFlow(files, targetType);
  };

  const switchPendingType = () => {
    if (!pendingUpload) return;
    const nextType = pendingUpload.targetType === "regular" ? "imaging" : "regular";
    const nextHistory = typeHistory.slice(0, typeHistoryIndex + 1).concat(nextType);
    setTypeHistory(nextHistory);
    setTypeHistoryIndex(nextHistory.length - 1);
    setPendingUpload((prev) => ({ ...prev, targetType: nextType }));
  };

  const undoTypeChoice = () => {
    if (typeHistoryIndex <= 0 || !pendingUpload) return;
    const nextIndex = typeHistoryIndex - 1;
    const prevType = typeHistory[nextIndex];
    setTypeHistoryIndex(nextIndex);
    setPendingUpload((prev) => ({ ...prev, targetType: prevType }));
  };

  const redoTypeChoice = () => {
    if (typeHistoryIndex >= typeHistory.length - 1 || !pendingUpload) return;
    const nextIndex = typeHistoryIndex + 1;
    const nextType = typeHistory[nextIndex];
    setTypeHistoryIndex(nextIndex);
    setPendingUpload((prev) => ({ ...prev, targetType: nextType }));
  };

  const confirmPendingUpload = async () => {
    if (!pendingUpload?.files?.length) return;
    if (pendingUpload.targetType === "imaging") {
      toast.info(t("Opening Pacsee in a new tab."));
      setPendingUpload(null);
      setTypeHistory([]);
      setTypeHistoryIndex(-1);
      await uploadMRI();
      return;
    }
    uploadFiles(pendingUpload.files);
    setPendingUpload(null);
    setTypeHistory([]);
    setTypeHistoryIndex(-1);
  };

  const cancelPendingUpload = () => {
    setPendingUpload(null);
    setTypeHistory([]);
    setTypeHistoryIndex(-1);
  };

  const openDocument = async (path) => {
    try {
      const response = await documentView({ url: path });
      window.open(response.data);
    } catch (err) {
      // no-op
    }
  };

  const deleteDocument = async () => {
    if (!confirmDeleteId) return;
    try {
      const response = await authenticatedRequest({
        method: "delete",
        url: `/admin/deleteDocumentsByDocumentId/${confirmDeleteId}`,
        headers: { "Content-Type": "application/json" },
      });
      if ([200, 201, 203].includes(response.status)) {
        toast.success(t(response.data.msg));
        setDocuments((prev) => prev.filter((d) => d._id !== confirmDeleteId));
      }
    } catch (err) {
      // no-op
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const uploadMRI = async () => {
    const requestUserId = clientId || localStorage.getItem("userId");
    if (!requestUserId) {
      toast.error(t("Unable to upload imaging - missing user id"));
      return;
    }
    try {
      setUploadLoading(true);
      const response = await authenticatedRequest({
        method: "get",
        url: `/users/uploadPacssee?userId=${requestUserId}`,
        headers: { "Content-Type": "application/json" },
      });
      if (response?.data?.url) {
        window.open(response.data.url, "_blank");
      } else if (response?.data?.msg) {
        toast.error(t(response.data.msg));
      }
    } catch (err) {
      // no-op
    } finally {
      setUploadLoading(false);
    }
  };

  const emptyState = !loading && documents.length === 0 && imagingDocs.length === 0;

  return (
    <>
      <Dialog
        dir="ltr"
        open={documentModal}
        onClose={() => {
          setDocumentModal(false);
          if (setPacseeDocument) setPacseeDocument([]);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pb: 1 }}>
          Documents &amp; imaging
          <Typography
            variant="body2"
            color="text.secondary"
            component="div"
            sx={{ mt: 1, fontWeight: 400, lineHeight: 1.45 }}
          >
            Use the <strong>left</strong> box for PDFs and general files. Use the{" "}
            <strong>right</strong> box for imaging-related uploads. Files you add show up in the
            list below.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(event) => onPickFiles(event, "regular")}
          />
          <input
            ref={imagingInputRef}
            type="file"
            multiple
            hidden
            onChange={(event) => onPickFiles(event, "imaging")}
          />
          {uploadLoading ? (
            <Box mb={1} display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="caption" color="text.secondary">
                Working…
              </Typography>
            </Box>
          ) : null}

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mb={2}>
            <Paper
              variant="outlined"
              onDragEnter={(event) => {
                event.preventDefault();
                setIsDraggingRegular(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDraggingRegular(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setIsDraggingRegular(false);
              }}
              onDrop={(event) => onDropFiles(event, "regular")}
              sx={{
                p: 2,
                flex: 1,
                borderStyle: "dashed",
                borderWidth: 2,
                borderColor: isDraggingRegular ? "primary.main" : "divider",
                bgcolor: isDraggingRegular ? "rgba(25,118,210,0.06)" : "background.paper",
                transition: "all 0.2s ease",
                textAlign: "center",
              }}
            >
              <FolderOpenOutlinedIcon sx={{ mb: 0.5, color: "text.secondary" }} />
              <Typography variant="body2" fontWeight={700}>
                Regular Documents
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                PDFs, Word, images, etc.
              </Typography>
              <Box mt={1.5}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CloudUploadOutlinedIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose files
                </Button>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              onDragEnter={(event) => {
                event.preventDefault();
                setIsDraggingImaging(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDraggingImaging(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setIsDraggingImaging(false);
              }}
              onDrop={(event) => onDropFiles(event, "imaging")}
              sx={{
                p: 2,
                flex: 1,
                borderStyle: "dashed",
                borderWidth: 2,
                borderColor: isDraggingImaging ? "#7c3aed" : "divider",
                bgcolor: isDraggingImaging ? "rgba(124,58,237,0.08)" : "background.paper",
                transition: "all 0.2s ease",
                textAlign: "center",
              }}
            >
              <MedicalServicesOutlinedIcon sx={{ mb: 0.5, color: "#7c3aed" }} />
              <Typography variant="body2" fontWeight={700}>
                Imaging (X-Ray / MRI / CT)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Imaging files for this meeting
              </Typography>
              <Box mt={1.5}>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<ImageSearchIcon />}
                  onClick={() => imagingInputRef.current?.click()}
                >
                  Choose imaging files
                </Button>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<MedicalServicesOutlinedIcon />}
                onClick={uploadMRI}
                disabled={uploadLoading}
                sx={{ mt: 2 }}
              >
                {t("Open Pacsee")}
              </Button>
            </Paper>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {loading ? (
            <Box py={4} display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {emptyState ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: "center" }}>
                  Nothing uploaded yet — use the two boxes above.
                </Typography>
              ) : null}

              {documents.length > 0 ? (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Documents
                  </Typography>
                  <List dense>
                    {documents.map((doc) => (
                      <ListItem
                        key={doc._id}
                        divider
                        secondaryAction={
                          <Stack direction="row" spacing={0.5}>
                            <IconButton
                              edge="end"
                              onClick={() => openDocument(doc.path)}
                              disabled={meetingType === "PrescriptionOnly"}
                            >
                              <RemoveRedEyeIcon />
                            </IconButton>
                            <IconButton edge="end" onClick={() => setConfirmDeleteId(doc._id)}>
                              <DeleteIcon sx={{ color: "#dc2626" }} />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          primary={doc.name}
                          secondary={doc.fileType || ""}
                          primaryTypographyProps={{ sx: { wordBreak: "break-all" } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : null}

              {imagingDocs.length > 0 ? (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                    Imaging
                  </Typography>
                  <List dense>
                    {imagingDocs.map((img, idx) => (
                      <ListItem
                        key={`${img.study_descr || "img"}-${idx}`}
                        divider
                        secondaryAction={
                          <IconButton edge="end" onClick={() => window.open(img.view_url_options)}>
                            <RemoveRedEyeIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={img.study_descr || `Imaging ${idx + 1}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {autoShowUploadHint ? (
            <Typography variant="caption" color="text.secondary" sx={{ mr: "auto", ml: 1 }}>
              No existing files - upload mode opened automatically
            </Typography>
          ) : null}
          <Button
            onClick={() => {
              setDocumentModal(false);
              if (setPacseeDocument) setPacseeDocument([]);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        dir="ltr"
        open={Boolean(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}
      >
        <DialogTitle>{t("Are you sure")}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>{t("No")}</Button>
          <Button color="error" variant="contained" onClick={deleteDocument}>
            {t("Yes")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        dir="ltr"
        open={Boolean(pendingUpload)}
        onClose={cancelPendingUpload}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirm file type</DialogTitle>
        <DialogContent>
          <Typography variant="body1" mb={1}>
            {pendingUpload?.targetType === "imaging"
              ? "Are you sure these are imaging files?"
              : "Are you sure these are regular documents?"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pendingUpload?.targetType === "imaging"
              ? "Choose Yes to continue with imaging workflow, or switch to regular documents."
              : "Choose Yes to upload as regular documents, or switch to imaging workflow."}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={undoTypeChoice}
              disabled={typeHistoryIndex <= 0}
              aria-label="Undo upload type"
            >
              <UndoOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={redoTypeChoice}
              disabled={typeHistoryIndex < 0 || typeHistoryIndex >= typeHistory.length - 1}
              aria-label="Redo upload type"
            >
              <RedoOutlinedIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              Undo/redo type selection until you confirm.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelPendingUpload}>Cancel</Button>
          <Button onClick={switchPendingType} variant="outlined">
            {pendingUpload?.targetType === "imaging"
              ? "No - switch to regular"
              : "No - switch to imaging"}
          </Button>
          <Button onClick={confirmPendingUpload} variant="contained" disabled={uploadLoading}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {uploadQueue.length > 0 || lastUploadResult ? (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            right: 20,
            bottom: 20,
            width: 330,
            maxWidth: "calc(100vw - 32px)",
            p: 1.5,
            zIndex: 1400,
            borderRadius: 2,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="subtitle2" fontWeight={700}>
              Upload status
            </Typography>
            <Button
              size="small"
              onClick={() => {
                setUploadQueue((prev) => prev.filter((item) => item.status === "uploading"));
                setLastUploadResult(null);
              }}
            >
              Clear
            </Button>
          </Stack>
          <Stack spacing={0.75}>
            {uploadQueue.slice(-4).map((item) => (
              <Box
                key={item.id}
                sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" fontWeight={600}>
                    {item.fileCount} file{item.fileCount > 1 ? "s" : ""}
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      item.status === "uploading"
                        ? "Uploading..."
                        : item.status === "done"
                        ? "Done"
                        : "Failed"
                    }
                    color={
                      item.status === "uploading"
                        ? "info"
                        : item.status === "done"
                        ? "success"
                        : "error"
                    }
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {item.names.join(", ")}
                  {item.fileCount > item.names.length ? "..." : ""}
                </Typography>
              </Box>
            ))}
            {lastUploadResult ? (
              <Alert severity={lastUploadResult.type}>{lastUploadResult.message}</Alert>
            ) : null}
          </Stack>
        </Paper>
      ) : null}
    </>
  );
}
